import React, {createContext, useState, useEffect, ReactNode} from 'react';

import {AuthContext, useAuth} from './AuthContext';
import {checkAuthentication} from '../../checkAuthentication';
import {decryptJson} from '../../decryption';


// Define the context value type
interface AuthStatusContextType {
    isAuthenticated: boolean;
    setIsAuthenticated: (tempIsAuthenticated: boolean) => void;
    isStaff: boolean;
    setIsStaff: (tempIsStaff: boolean) => void;
    isSuperuser: boolean;
    setIsSuperuser: (tempIsSuperuser: boolean) => void;
}


// Create the context with an initial value
const AuthStatusContext = createContext<AuthStatusContextType | undefined>(undefined);


export const useAuthStatus = () => {
    const context = React.useContext(AuthStatusContext);
    if (!context) {
        throw new Error('useAuthStatus must be used within an AuthStatusProvider');
    }
    return context;
};


interface AuthStatusProviderProps {
    children: ReactNode;
}


export const AuthStatusProvider: React.FC<AuthStatusProviderProps> = ({children}) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isStaff, setIsStaff] = useState<boolean>(false);
    const [isSuperuser, setIsSuperuser] = useState<boolean>(false);
    const {user} = useAuth();


    useEffect(() => {
        try {
            if (user !== null && user !== "") {
                setIsAuthenticated(checkAuthentication(user));
                setIsStaff(decryptJson(user, process.env.NEXT_PUBLIC_SECRET_KEY).is_staff);
                setIsSuperuser(decryptJson(user, process.env.NEXT_PUBLIC_SECRET_KEY).is_superuser);
            } else {
                setIsAuthenticated(false);
                setIsStaff(false);
                setIsSuperuser(false);
            }
        } catch (error) {
            setIsAuthenticated(false);
            setIsStaff(false);
            setIsSuperuser(false);
        }
    }, [user]);


    return (
        <AuthStatusContext.Provider value={{
            isAuthenticated,
            setIsAuthenticated,
            isStaff,
            setIsStaff,
            isSuperuser,
            setIsSuperuser
        }}>
            {children}
        </AuthStatusContext.Provider>
    );
};
